import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../../store";
import LoaderWithContainer from "../../../components/base-components/LoaderWithContainer";
import ErrorMessageWithContainer from "../../../components/base-components/ErrorWithConatiner";
import NoData from "../../../components/base-components/NoData";
import DevelopmentUpdatesViewHeader from "./DevelopmentUpdatesViewHeader";
import DevelopmentUpdateItem from "./DevelopmentUpdateItem";
import { Helmet } from "react-helmet-async";

const DevelopmentUpdatesView: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { updates, buyer, project, status, error } = useSelector(
    (state: RootState) => state.developmentUpdates
  );

  useEffect(() => {
    // Only redirect if status is idle AND we have no data (user came directly to URL without auth)
    // Don't redirect if loading or if we have successful data
    if (status === "idle" && !updates.length && !buyer && !project) {
      navigate("/development-updates");
      return;
    }

    // If we have data, verify projectId matches (optional check)
    if (projectId && project && status === "succeeded") {
      if (project.id.toString() !== projectId) {
        navigate("/development-updates");
        return;
      }
    }
  }, [status, updates.length, buyer, project, projectId, navigate]);

  // Don't clear on unmount - keep data in case user navigates back
  // Only clear when explicitly needed (e.g., logout)

  if (status === "loading") return <LoaderWithContainer />;
  if (status === "failed")
    return (
      <ErrorMessageWithContainer
        message={error || "Failed to load development updates"}
      />
    );
  if (!updates.length || !buyer || !project) {
    return <NoData message="No development updates found" />;
  }

  // Sort by updateDate (newest first), nulls go to the end
  const sortedUpdates = [...updates].sort((a, b) => {
    // If both have updateDate, sort by date (newest first)
    if (a.updateDate && b.updateDate) {
      return (
        new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
      );
    }
    // If only a has updateDate, it comes first
    if (a.updateDate && !b.updateDate) {
      return -1;
    }
    // If only b has updateDate, it comes first
    if (!a.updateDate && b.updateDate) {
      return 1;
    }
    // If neither has updateDate, maintain original order
    return 0;
  });

  return (
    <>
      <Helmet>
        <title>Development Updates - DanCap Group</title>
        <meta
          name="description"
          content="Your development updates and project information"
        />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <section className="global-container">
        <DevelopmentUpdatesViewHeader
          title={project.title}
          depositPaid={buyer.DepositPaid}
          purchasePrice={buyer.PurchasePrice}
          buyerName={buyer.BuyerName}
          plotNumber={buyer.PlotNumber}
        />

        {/* Development Updates Feed - Newest first */}
        <div className="flex flex-col">
          {sortedUpdates.map((update, index) => (
            <DevelopmentUpdateItem
              key={update.id}
              update={update}
              isFirst={index === 0}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default DevelopmentUpdatesView;
